import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Factory, X, Plus, Loader2, CheckCircle2, ArrowRight, ChevronLeft, ShieldCheck, FileText } from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { languageForCountry } from '@/i18n';
import NdaSignDialog from '@/components/NdaSignDialog';

const DEFAULT_SPECIALTIES = [
  'Usinage', 'Tournage', 'Fraisage', 'Décolletage',
  'Tôlerie', 'Pliage', 'Découpe laser', 'Soudure',
  'Impression 3D', 'Prototypage rapide', 'Moulage',
  'Injection plastique', 'Fonderie', 'Mécano-soudure',
  'Traitement de surface', 'Anodisation', 'Peinture industrielle',
  'Assemblage', 'Montage', 'Câblage électrique', 'Usinage CNC 5 axes',
];

const COUNTRIES = [
  'France', 'Belgique', 'Suisse', 'Luxembourg', 'Allemagne', 'Italie',
  'Espagne', 'Portugal', 'Pays-Bas', 'Royaume-Uni', 'Pologne',
  'République tchèque', 'Roumanie', 'Maroc', 'Tunisie', 'Algérie',
  'Turquie', 'Chine', 'Vietnam', 'Inde', 'États-Unis', 'Canada',
];

export default function RegisterSubcontractorPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    company_name: '', contact_name: '', email: '', password: '',
    phone: '', address: '', country: 'France',
  });
  const [specialties, setSpecialties] = useState([]);
  const [customSpec, setCustomSpec] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [ndaAcknowledged, setNdaAcknowledged] = useState(false);
  const [ndaPreviewOpen, setNdaPreviewOpen] = useState(false);

  const toggleSpec = (s) => {
    setSpecialties(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  const addCustomSpec = () => {
    const v = customSpec.trim();
    if (!v) return;
    if (specialties.includes(v)) return;
    setSpecialties(prev => [...prev, v]);
    setCustomSpec('');
  };

  const onCountryChange = (country) => {
    setForm(f => ({ ...f, country }));
    const lang = languageForCountry(country);
    if (lang && lang !== i18n.language) i18n.changeLanguage(lang);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.email || !form.password
      || !form.phone || !form.address || !form.country || specialties.length === 0) {
      toast.error(t('register_sub.required_all'));
      return;
    }
    if (!ndaAcknowledged) {
      toast.error(i18n.language === 'fr'
        ? "Vous devez prendre connaissance du contrat de confidentialité"
        : 'You must acknowledge the confidentiality agreement');
      return;
    }
    setSubmitting(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${API}/api/auth/register-subcontractor`, {
        ...form,
        specialties,
        language: i18n.language,
      });
      setSuccess(true);
      toast.success(t('register_sub.success'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-6">
        <div className="absolute top-5 right-6"><LanguageSwitcher /></div>
        <div className="w-full max-w-md bg-zinc-900/40 border border-zinc-800 rounded-md p-8 text-center">
          <div className="inline-flex w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/30 items-center justify-center mb-5">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
          </div>
          <h2 className="text-xl font-semibold mb-2 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            {t('register_sub.success')}
          </h2>
          <p className="text-sm text-zinc-400 mb-7 leading-relaxed">{t('pending.desc')}</p>
          <Button
            onClick={() => navigate('/login')}
            className="w-full bg-zinc-100 hover:bg-white text-zinc-950 rounded-md font-medium h-10"
          >
            {t('login.title')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="register-sub-page">
      <div className="absolute top-5 right-6 z-30"><LanguageSwitcher /></div>

      {/* Top bar with back link */}
      <div className="border-b border-zinc-900">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-100 transition-colors" data-testid="back-to-login">
            <ChevronLeft className="w-4 h-4" /> {t('register_sub.cancel')}
          </Link>
          <Link to="/login" className="flex items-center gap-2 group">
            <img
              src="https://customer-assets.emergentagent.com/job_nexus-prod-preview/artifacts/afn21evr_3a25046d-0d87-46da-bb63-a34aac091e27.png"
              alt="ProdPilot"
              className="w-7 h-7 rounded-sm object-contain"
            />
            <span className="text-base font-semibold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              <span className="text-zinc-50">Prod</span><span className="text-blue-400">Pilot</span>
            </span>
          </Link>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Title block */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-zinc-900/60 border border-zinc-800 rounded-full mb-5 text-[11px] uppercase tracking-wider text-zinc-400">
            <Factory className="w-3 h-3 text-amber-400" />
            Espace sous-traitant
          </div>
          <h1 className="text-4xl font-semibold tracking-tight text-zinc-50 mb-3 leading-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            {t('register_sub.title')}
          </h1>
          <p className="text-sm text-zinc-500 leading-relaxed max-w-lg">
            {t('register_sub.subtitle')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="space-y-8">
          {/* Section 1 — Identity */}
          <section>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-4">{t('register_sub.section_identity')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-zinc-400">{t('register_sub.company_name')} *</Label>
                <Input required value={form.company_name}
                  onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))}
                  className="bg-transparent border-zinc-800 hover:border-zinc-700 focus-visible:border-zinc-600 focus-visible:ring-0 text-zinc-100 rounded-md h-10 text-sm"
                  data-testid="rs-company-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-zinc-400">{t('register_sub.contact_name')} *</Label>
                <Input required value={form.contact_name}
                  onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))}
                  className="bg-transparent border-zinc-800 hover:border-zinc-700 focus-visible:border-zinc-600 focus-visible:ring-0 text-zinc-100 rounded-md h-10 text-sm"
                  data-testid="rs-contact-name" />
              </div>
            </div>
          </section>

          {/* Section 2 — Account */}
          <section>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-4">{t('register_sub.section_account')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-zinc-400">{t('register_sub.email')} *</Label>
                <Input required type="email" value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="bg-transparent border-zinc-800 hover:border-zinc-700 focus-visible:border-zinc-600 focus-visible:ring-0 text-zinc-100 rounded-md h-10 text-sm"
                  data-testid="rs-email" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-zinc-400">{t('register_sub.password')} *</Label>
                <Input required type="password" minLength={6} value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  className="bg-transparent border-zinc-800 hover:border-zinc-700 focus-visible:border-zinc-600 focus-visible:ring-0 text-zinc-100 rounded-md h-10 text-sm"
                  data-testid="rs-password" />
              </div>
            </div>
          </section>

          {/* Section 3 — Contact */}
          <section>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-4">{t('register_sub.section_contact')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-zinc-400">{t('register_sub.phone')} *</Label>
                <Input required value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  className="bg-transparent border-zinc-800 hover:border-zinc-700 focus-visible:border-zinc-600 focus-visible:ring-0 text-zinc-100 rounded-md h-10 text-sm"
                  data-testid="rs-phone" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-zinc-400">{t('register_sub.country')} *</Label>
                <select required value={form.country} onChange={e => onCountryChange(e.target.value)}
                  className="w-full bg-transparent border border-zinc-800 hover:border-zinc-700 focus:border-zinc-600 focus:outline-none text-zinc-100 rounded-md h-10 px-3 text-sm transition-colors"
                  data-testid="rs-country">
                  {COUNTRIES.map(c => <option key={c} value={c} className="bg-zinc-900">{c}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2 space-y-1.5">
                <Label className="text-xs font-medium text-zinc-400">{t('register_sub.address')} *</Label>
                <Input required value={form.address}
                  onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                  className="bg-transparent border-zinc-800 hover:border-zinc-700 focus-visible:border-zinc-600 focus-visible:ring-0 text-zinc-100 rounded-md h-10 text-sm"
                  data-testid="rs-address" />
              </div>
            </div>
          </section>

          {/* Section 4 — Specialties */}
          <section>
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{t('register_sub.section_specialties')} *</h2>
              <span className="text-[11px] text-zinc-600">{specialties.length} {t('register_sub.selected_count')}</span>
            </div>
            <p className="text-xs text-zinc-500 mb-4">{t('register_sub.specialties_hint')}</p>
            <div className="flex flex-wrap gap-1.5" data-testid="rs-specialty-chips">
              {DEFAULT_SPECIALTIES.map(s => {
                const active = specialties.includes(s);
                return (
                  <button key={s} type="button" onClick={() => toggleSpec(s)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                      active
                        ? 'bg-zinc-100 border-zinc-100 text-zinc-950 font-medium'
                        : 'bg-transparent border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200'
                    }`}
                    data-testid={`rs-spec-${s.replace(/\s+/g, '-').toLowerCase()}`}
                  >{s}</button>
                );
              })}
              {specialties.filter(s => !DEFAULT_SPECIALTIES.includes(s)).map(s => (
                <span key={s} className="text-xs px-3 py-1.5 rounded-full bg-zinc-100 border border-zinc-100 text-zinc-950 inline-flex items-center gap-1.5 font-medium">
                  {s}
                  <button type="button" onClick={() => toggleSpec(s)} className="hover:text-rose-600">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <Input
                value={customSpec}
                onChange={e => setCustomSpec(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomSpec(); } }}
                placeholder={t('register_sub.custom_specialty')}
                className="bg-transparent border-zinc-800 hover:border-zinc-700 focus-visible:border-zinc-600 focus-visible:ring-0 text-zinc-100 h-9 text-sm rounded-md flex-1"
                data-testid="rs-custom-spec-input"
              />
              <Button type="button" onClick={addCustomSpec} size="sm" variant="outline"
                className="border-zinc-800 text-zinc-300 hover:bg-zinc-900 h-9 rounded-md" data-testid="rs-add-spec-btn">
                <Plus className="w-3 h-3 mr-1" /> {t('register_sub.add_specialty')}
              </Button>
            </div>
          </section>

          {/* NDA acknowledgement */}
          <section className="pt-4 border-t border-zinc-900" data-testid="rs-nda-section">
            <div className="bg-amber-500/5 border border-amber-500/30 rounded-md p-4">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-amber-100">
                    {i18n.language === 'fr' ? 'Contrat de confidentialité' : 'Confidentiality Agreement'}
                  </h3>
                  <p className="text-xs text-amber-200/80 mt-1 leading-relaxed">
                    {i18n.language === 'fr'
                      ? "ProdLink protège les plans techniques et les coordonnées de ses clients finaux. Un contrat de confidentialité (non-divulgation des plans + non-sollicitation du client final pendant 3 ans) doit être lu et signé électroniquement lors de votre première connexion après validation de votre compte."
                      : 'ProdLink protects technical drawings and end-client information. A confidentiality agreement (non-disclosure of drawings + non-solicitation of the end client for 3 years) must be read and electronically signed at your first login after account validation.'}
                  </p>
                  <button type="button" onClick={() => setNdaPreviewOpen(true)}
                    className="inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 mt-2 underline underline-offset-2"
                    data-testid="rs-nda-preview-btn">
                    <FileText className="w-3.5 h-3.5" />
                    {i18n.language === 'fr' ? 'Lire le contrat maintenant' : 'Read the contract now'}
                  </button>
                  <label className="flex items-start gap-2 text-xs text-zinc-200 mt-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={ndaAcknowledged}
                      onChange={(e) => setNdaAcknowledged(e.target.checked)}
                      className="mt-0.5 accent-amber-500"
                      data-testid="rs-nda-ack"
                    />
                    <span>
                      {i18n.language === 'fr'
                        ? "Je reconnais avoir pris connaissance du contrat de confidentialité et m'engage à le signer électroniquement lors de ma première connexion."
                        : 'I acknowledge having reviewed the confidentiality agreement and commit to signing it electronically at my first login.'}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </section>

          {/* Footer */}
          <div className="pt-4 border-t border-zinc-900 space-y-4">
            <p className="text-[11px] text-zinc-500 italic">{t('register_sub.terms')}</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => navigate('/login')}
                className="border-zinc-800 text-zinc-300 hover:bg-zinc-900 rounded-md sm:flex-1 h-10">
                {t('register_sub.cancel')}
              </Button>
              <Button type="submit" disabled={submitting}
                className="bg-zinc-100 hover:bg-white text-zinc-950 font-medium rounded-md sm:flex-1 h-10 group" data-testid="rs-submit-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {t('register_sub.submit')}
                {!submitting && <ArrowRight className="w-3.5 h-3.5 ml-1.5 group-hover:translate-x-0.5 transition-transform" />}
              </Button>
            </div>
            <div className="text-center text-xs text-zinc-500 pt-2">
              {t('register_sub.already')}{' '}
              <Link to="/login" className="text-zinc-300 hover:text-zinc-100 underline underline-offset-2">{t('register_sub.login_link')}</Link>
            </div>
          </div>
        </form>
      </div>

      {/* NDA preview (read-only) */}
      <NdaSignDialog
        open={ndaPreviewOpen}
        onClose={() => setNdaPreviewOpen(false)}
        readOnly
        defaultName={form.contact_name}
        companyName={form.company_name}
      />
    </div>
  );
}
